// Shared premium checkout helper.
// RULE: low-ticket, high-frequency plans (Lite & Monthly) are sold as a
// Razorpay AUTO-PAY MANDATE so every purchase creates a recurring subscription
// (grows the mandate base). Longer plans stay one-time at their own surfaces.
//
// This mirrors the proven, live mandate flow used in GoPremiumPage:
//   create-mandate -> Razorpay checkout in subscription mode -> verify-mandate
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = "rzp_live_OPQaGpWtnwCBjC";

// Plans that MUST go through the auto-pay mandate flow.
export const MANDATE_PLANS = ["lite_monthly", "monthly", "half_yearly", "yearly"];

export const isMandatePlan = (planId) => MANDATE_PLANS.includes(planId);

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => {
      toast.error("Payment gateway failed to load. Check your connection and try again.");
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

/**
 * Open a Razorpay AUTO-PAY mandate checkout for Lite/Monthly.
 * @param {{id:string,name:string}} plan
 * @param {object} user  - must have id, name, email, phone
 * @param {string|null} discountCode
 * @param {(verifyData:object)=>void} onSuccess - called after verify succeeds
 * @param {()=>void} onClose - called on dismiss / failure (reset processing state)
 */
export async function openMandateCheckout({ plan, user, discountCode = null, onSuccess, onClose }) {
  const loaded = await loadRazorpay();
  if (!loaded) {
    onClose && onClose();
    return;
  }

  let data;
  try {
    const resp = await fetch(`${API_URL}/api/premium-plan/create-mandate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, plan_id: plan.id, discount_code: discountCode }),
    });
    data = await resp.json();
  } catch (e) {
    toast.error("Network error starting subscription. Please try again.");
    onClose && onClose();
    return;
  }

  if (!data.success || !data.subscription_id) {
    toast.error(data.error || data.detail || "Failed to start auto-pay subscription.");
    onClose && onClose();
    return;
  }

  const options = {
    key: data.key_id || RAZORPAY_KEY,
    // Passing subscription_id (NOT order_id) opens Razorpay in mandate mode:
    // the card/UPI is saved and auto-charged every cycle.
    subscription_id: data.subscription_id,
    name: "Pinnacle Vastu Premium",
    description: `${plan.name} — auto-renews every cycle. Cancel anytime.`,
    prefill: {
      name: user.name || "",
      email: user.email || "",
      contact: user.phone || "",
    },
    theme: { color: "#F59E0B" },
    // Prefer UPI AutoPay (highest e-mandate completion rate in India) while
    // keeping card & other methods as fallback (show_default_blocks: true).
    config: {
      display: {
        blocks: {
          upi_autopay: {
            name: "Pay via UPI AutoPay (recommended)",
            instruments: [{ method: "upi" }],
          },
        },
        sequence: ["block.upi_autopay"],
        preferences: { show_default_blocks: true },
      },
    },
    handler: async function (response) {
      toast.loading("Activating your premium subscription...");
      try {
        const verifyResp = await fetch(`${API_URL}/api/premium-plan/verify-mandate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
            user_id: user.id,
            plan_id: plan.id,
          }),
        });
        const verifyData = await verifyResp.json();
        toast.dismiss();
        if (verifyData.success) {
          toast.success(`Welcome to Premium! ${verifyData.credits_granted || 0} credits added. Auto-renews next cycle.`);
          onSuccess && onSuccess(verifyData);
        } else {
          toast.error(verifyData.detail || verifyData.error || "Payment verification failed.");
          onClose && onClose();
        }
      } catch (e) {
        toast.dismiss();
        toast.error("Error activating subscription. Please contact support with your payment id.");
        onClose && onClose();
      }
    },
    modal: {
      ondismiss: function () {
        onClose && onClose();
        toast.info("Payment cancelled");
      },
    },
  };

  const razorpay = new window.Razorpay(options);
  razorpay.on("payment.failed", function (resp) {
    toast.error(`Auto-pay setup failed: ${resp?.error?.description || "your bank may not support e-mandate"}.`, { duration: 6000 });
    onClose && onClose();
  });
  razorpay.open();
}
